import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PanelHead, PillButton, Face, useArmed, PINNED, MUTED, SUBTLE, RED, CYAN, MAGENTA, textareaStyle } from './clubUi';
import ClubPollBlock from './ClubPollBlock';
import ClubEventRow from './ClubEventRow';
import clubService from '../../services/clubService';
import { ago } from '../../utils/surfaceFormat';
import { posterPlaceholder } from '../../utils/posterPlaceholder';

/**
 * One card in the POSTS feed (README §1): a white panel titled by kind — PINNED (#FFF8EA) ·
 * ANNOUNCEMENT · NEW OFFER (a linked Pro page's offer, with a CLAIM row) · PORTAL · POLL ·
 * EVENT LINKED · GATHERING · NEW THREAD · JOINED — with the time
 * on the right, and for admins the text actions PIN/UNPIN · EDIT · DELETE (armed to CONFIRM DELETE).
 *
 * Reactions — the four emoji reactions stay on announcement cards only (the characters are what
 * the backend groups on); replies are an expandable thread that hides itself when the endpoint
 * is not there.
 */
export const REACTIONS = ['🔥', '❤️', '💯', '🎉'];

const KIND_TITLE = { PINNED: 'PINNED', POST: 'ANNOUNCEMENT', OFFER: 'NEW OFFER', EVENT: 'EVENT LINKED', GATHERING: 'GATHERING', POLL: 'POLL', PORTAL: 'PORTAL', FORUM: 'NEW THREAD', JOINED: 'NEW THIS WEEK' };

function LinkRow({ link, cyanRing = false, size = 40 }) {
  const navigate = useNavigate();
  if (!link) return null;
  const big = size > 40; // the gathering card: a 72px thumbnail in a radius-20 block (handoff §5)
  return (
    <button type="button" onClick={() => navigate(link.to)} className="flex w-full items-center text-left font-mono" style={{ gap: big ? 14 : 12, padding: big ? 8 : '10px 14px 10px 10px', borderRadius: big ? 20 : 18, background: '#F5F5F5', border: 'none', cursor: 'pointer', color: 'inherit' }} data-testid="feed-link">
      <span className="flex-none overflow-hidden" style={{ width: size, height: size, borderRadius: cyanRing ? '50%' : 14, background: link.imageUrl ? '#111' : posterPlaceholder(link.key || link.label), boxShadow: cyanRing ? `0 0 0 2px ${CYAN}` : 'none' }}>
        {link.imageUrl && <img src={link.imageUrl} alt="" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
      </span>
      <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 3 }}>
        <span className="truncate" style={{ fontSize: big ? 14 : 12, fontWeight: 700, lineHeight: 1.4 }}>{link.label}</span>
        <span className="truncate uppercase" style={{ fontSize: big ? 10 : 9, fontWeight: 700, letterSpacing: big ? '.1em' : '.14em', color: MUTED }}>{link.note}</span>
      </span>
      {big ? <span style={{ paddingRight: 10, fontSize: 16, fontWeight: 800 }} aria-hidden="true">→</span> : <PillButton size={34} fontSize={9}>OPEN</PillButton>}
    </button>
  );
}

function Replies({ announcementId, currentUserId, canReply, onCountChange, onUnsupported }) {
  const [replies, setReplies] = useState(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const unsupportedRef = useRef(onUnsupported);
  useEffect(() => { unsupportedRef.current = onUnsupported; }, [onUnsupported]);

  useEffect(() => {
    let done = false;
    clubService.listAnnouncementReplies(announcementId).then((r) => {
      if (done) return;
      if (r.success) setReplies(r.data?.items || r.data || []);
      else unsupportedRef.current?.();
    });
    return () => { done = true; };
  }, [announcementId]);

  const send = async () => {
    const body = text.trim();
    if (!body || busy) return;
    setBusy(true); setError(null);
    const r = await clubService.addAnnouncementReply(announcementId, body);
    setBusy(false);
    if (r.success) { const next = [...(replies || []), r.data]; setReplies(next); setText(''); onCountChange?.(next.length); }
    else setError(r.error || 'Could not post the reply');
  };
  const remove = async (reply) => {
    const r = await clubService.deleteAnnouncementReply(reply.id);
    if (r.success) { const next = (replies || []).filter((x) => x.id !== reply.id); setReplies(next); onCountChange?.(next.length); }
  };

  return (
    <div className="flex flex-col" style={{ gap: 12, padding: 14, borderRadius: 18, background: '#F5F5F5' }} data-testid="feed-replies">
      {replies === null && <span className="uppercase" style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.1em', color: SUBTLE }}>LOADING…</span>}
      {replies?.length === 0 && <span style={{ fontSize: 12, color: MUTED }}>No replies yet.</span>}
      {replies?.map((r) => (
        <div key={r.id} className="flex" style={{ gap: 10 }} data-testid="feed-reply">
          <Face src={r.authorAvatarUrl} name={r.authorDisplayName} size={28} tintKey={r.authorUserId} />
          <div className="min-w-0 flex-1">
            <span style={{ fontSize: 12, fontWeight: 700 }}>{r.authorDisplayName}</span>
            <span style={{ fontSize: 11, color: SUBTLE }}> · {ago(r.createdAt)}</span>
            {(r.isMine || r.canModerate || r.authorUserId === currentUserId) && (
              <button type="button" onClick={() => remove(r)} className="ml-2 font-mono uppercase hover:underline" style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.14em', color: RED, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>DELETE</button>
            )}
            <div style={{ fontSize: 13, lineHeight: 1.55, marginTop: 2, whiteSpace: 'pre-wrap' }}>{r.body}</div>
          </div>
        </div>
      ))}
      {canReply && (
        <div className="flex" style={{ gap: 8 }}>
          <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="Reply…" maxLength={2000} aria-label="Reply" data-testid="reply-input"
            className="min-w-0 flex-1 font-mono" style={{ height: 40, padding: '0 16px', border: 'none', borderRadius: 999, background: '#fff', fontSize: 13, outline: 'none' }} />
          <PillButton size={40} fontSize={10} onClick={send} disabled={!text.trim() || busy} data-testid="reply-send">REPLY</PillButton>
        </div>
      )}
      {error && <span style={{ fontSize: 11, color: RED }}>{error}</span>}
    </div>
  );
}

export default function ClubFeedPost({
  item, state, currentUserId, mobile = false,
  onReact, onEdit, onDelete, onTogglePin, onDeletePoll, onVote, onHideEvent, onUnlinkEvent,
}) {
  const [repliesOpen, setRepliesOpen] = useState(false);
  const [repliesUnsupported, setRepliesUnsupported] = useState(false);
  const [replyCount, setReplyCount] = useState(item.announcement?.replyCount ?? null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [draftPinned, setDraftPinned] = useState(false);
  const { isArmed, confirm } = useArmed();

  const a = item.announcement;
  const isAnnouncement = item.kind === 'POST' || item.kind === 'PINNED' || item.kind === 'OFFER';
  const isAuthor = !!(a && currentUserId && a.authorUserId === currentUserId);
  const canModify = isAnnouncement && (state.isAdmin || isAuthor);

  const actions = [];
  if (isAnnouncement && canModify) {
    if (state.isAdmin) actions.push({ label: a.isPinned ? 'UNPIN' : 'PIN', onClick: () => onTogglePin?.(a) });
    actions.push({ label: 'EDIT', onClick: () => { setDraft(a.content); setDraftPinned(!!a.isPinned); setEditing(true); } });
    actions.push({ label: 'DELETE', danger: true, arm: 'delete', onClick: () => onDelete?.(a) });
  } else if (item.kind === 'POLL' && state.isAdmin) {
    actions.push({ label: 'DELETE', danger: true, arm: 'delete', onClick: () => onDeletePoll?.(item.poll) });
  } else if (item.kind === 'EVENT' && state.isAdmin) {
    actions.push({ label: item.event.isHiddenInClub ? 'UNHIDE' : 'HIDE', onClick: () => onHideEvent?.(item.event) });
    actions.push({ label: 'UNLINK', danger: true, arm: 'unlink', onClick: () => onUnlinkEvent?.(item.event) });
  }

  const saveEdit = async () => {
    if (!draft.trim()) return;
    await onEdit?.(a, { content: draft.trim(), isPinned: draftPinned });
    setEditing(false);
  };

  const countLabel = replyCount == null ? 'REPLY' : replyCount === 1 ? '1 REPLY' : `${replyCount} REPLIES`;
  const title = KIND_TITLE[item.kind] || item.kind;
  const rightColor = item.kind === 'PORTAL' ? MAGENTA : SUBTLE;
  const showPerson = item.kind !== 'EVENT' && item.kind !== 'GATHERING' && item.kind !== 'JOINED';

  return (
    <article className="flex min-w-0 flex-col font-mono" style={{ background: item.kind === 'PINNED' ? PINNED : '#fff', borderRadius: mobile ? 22 : 24, padding: mobile ? '18px 16px' : '22px 24px', gap: 14 }} data-testid={`feed-${item.kind.toLowerCase()}`} data-kind={item.kind}>
      <PanelHead title={title} right={item.kind === 'PORTAL' ? 'LIVE' : item.when} rightColor={rightColor} />

      {showPerson && (
        <div className="flex" style={{ gap: 12 }}>
          <Face src={item.avatarUrl} name={item.who} size={40} tintKey={item.authorUserId || item.who} />
          <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 5 }}>
            <span className="flex flex-wrap items-baseline" style={{ gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 700 }}>{item.who}</span>
              <span style={{ fontSize: 11, color: SUBTLE }}>· {item.when}{item.edited ? ' · EDITED' : ''}</span>
            </span>
            {item.title && item.kind !== 'POLL' && <span style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.35 }}>{item.title}</span>}
            {editing ? (
              <div className="flex flex-col" style={{ gap: 8 }} data-testid="feed-edit">
                <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3} maxLength={2000} aria-label="Edit post" style={{ ...textareaStyle, padding: '12px 16px', borderRadius: 18, resize: 'none' }} />
                <div className="flex flex-wrap items-center" style={{ gap: 6 }}>
                  {state.isAdmin && <PillButton variant="tertiary" size={36} fontSize={10} weight={700} onClick={() => setDraftPinned((v) => !v)} aria-pressed={draftPinned} style={{ background: draftPinned ? PINNED : undefined }}>{draftPinned ? 'PINNED ✓' : 'PIN IT'}</PillButton>}
                  <span className="ml-auto flex" style={{ gap: 6 }}>
                    <PillButton variant="tertiary" size={36} fontSize={10} weight={700} onClick={() => setEditing(false)}>CANCEL</PillButton>
                    <PillButton size={36} fontSize={10} onClick={saveEdit} disabled={!draft.trim()} data-testid="feed-edit-save">SAVE</PillButton>
                  </span>
                </div>
              </div>
            ) : (
              item.body && <span style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{item.body}</span>
            )}
            {item.kind === 'POLL' && <ClubPollBlock poll={item.poll} canVote={state.isMember} onVote={onVote} mobile={mobile} />}
            {item.kind === 'FORUM' && item.link && <LinkRow link={item.link} />}
            {isAnnouncement && item.link && <LinkRow link={item.link} />}
            {item.kind === 'PORTAL' && item.link && <LinkRow link={{ ...item.link, imageUrl: item.avatarUrl }} cyanRing />}
            {isAnnouncement && (
              <span className="flex flex-wrap items-center" style={{ gap: 6, paddingTop: 4 }} data-testid="feed-reactions">
                {REACTIONS.map((emoji) => {
                  const r = (a.reactions || []).find((x) => x.emoji === emoji);
                  const on = !!r?.userReacted;
                  return (
                    <button key={emoji} type="button" onClick={() => onReact?.(a, emoji)} aria-pressed={on} aria-label={`React ${emoji}`} data-testid={`react-${emoji}`}
                      className="flex-none whitespace-nowrap font-mono" style={{ height: 30, padding: '0 10px', border: `2px solid ${on ? '#000' : 'transparent'}`, borderRadius: 999, background: on ? CYAN : '#F2F2F2', fontSize: 11, cursor: 'pointer', boxSizing: 'border-box' }}>
                      {emoji} {r?.count || 0}
                    </button>
                  );
                })}
                {!repliesUnsupported && (
                  <button type="button" onClick={() => setRepliesOpen((v) => !v)} className="ml-auto font-mono uppercase hover:underline" style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.14em', color: MUTED, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }} data-testid="feed-reply-toggle">
                    {repliesOpen ? 'HIDE REPLIES' : countLabel}
                  </button>
                )}
              </span>
            )}
            {actions.length > 0 && !editing && (
              <span className="flex flex-wrap" style={{ gap: 14, paddingTop: 2 }} data-testid="feed-actions">
                {actions.map((x) => {
                  const armed = x.arm && isArmed(x.arm);
                  return (
                    <button key={x.label} type="button" onClick={() => { if (x.arm && !confirm(x.arm)) return; x.onClick(); }} className="font-mono uppercase hover:underline" style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.14em', color: x.danger ? RED : '#000', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }} data-testid={`feed-action-${x.label}`}>
                      {armed ? `CONFIRM ${x.label}` : x.label}
                    </button>
                  );
                })}
              </span>
            )}
          </span>
        </div>
      )}

      {item.kind === 'EVENT' && <ClubEventRow event={item.event} phone={mobile} admin={state.isAdmin} armedUnlink={isArmed('unlink')} onHide={onHideEvent} onUnlink={(e) => { if (confirm('unlink')) onUnlinkEvent?.(e); }} />}
      {item.kind === 'GATHERING' && item.link && <LinkRow link={item.link} size={72} />}
      {item.kind === 'JOINED' && (
        <div className="flex flex-col" style={{ gap: 10 }}>
          <span style={{ fontSize: 14, lineHeight: 1.6 }}>{item.body}</span>
          {item.joined?.length > 0 && (
            <div className="flex flex-wrap" style={{ gap: 16 }}>
              {item.joined.slice(0, 8).map((m) => (
                <span key={m.userId} className="flex flex-col items-center text-center" style={{ gap: 6, width: 84 }}>
                  <Face src={m.avatarUrl} name={m.displayName} size={60} tintKey={m.userId} />
                  <span style={{ fontSize: 11, fontWeight: 700 }}>{(m.displayName || '').split(' ')[0]}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {isAnnouncement && repliesOpen && !repliesUnsupported && (
        <Replies announcementId={a.id} currentUserId={currentUserId} canReply={state.isMember} onCountChange={setReplyCount} onUnsupported={() => { setRepliesUnsupported(true); setRepliesOpen(false); }} />
      )}
    </article>
  );
}
